import { ENVIRONMENT_INITIALIZER, importProvidersFrom, inject } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { AuthenticationGuard, AuthorizationGuard } from '@softline/auth';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import {
  SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
  SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
  SOFTLINE_FEATURE_WORK_NOTE_KZWETTER,
  SOFTLINE_PERMISSION_WORK_NOTE,
  WorkNoteBewegungenStore,
  WorkNoteCollectionStore,
  WorkNoteKzWetterStore,
} from './arbeitsschein.shared';
import {
  SOFTLINE_API_WORK_NOTE_BEWEGUNGEN,
  SOFTLINE_API_WORK_NOTES,
} from './arbeitsschein.api';
import { WorkNoteService } from './services/work-note.service';
import { WorkNoteBewegungenService } from './services/work-note-bewegungen.service';
import { KzWetterService } from './services/kz-wetter.service';
import { HttpClient } from '@angular/common/http';
import { SignatureService } from './services/signature.service';
import { ArbeitsscheinConfig } from './arbeitsschein.config';
import { MasterDataResolver } from '@softline/application';

const ARBEITSSCHEIN_BEAN_ID = 'Softline.Objekt.StammTables.Arbeitsschein';

const WORK_NOTE_ROUTE_BASE: Route = {
  path: 'arbeitsschein',
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./components/work-note.component').then((o) => o.WorkNoteComponent),
      children: [
        {
          path: 'query',
          loadComponent: () =>
            import('./pages/work-note-query-page/work-note-query-page.component').then(
              (o) => o.WorkNoteQueryPageComponent
            ),
        },
        {
          path: ':id',
          loadComponent: () =>
            import('./pages/work-note-detail-page/work-note-detail-page.component').then(
              (o) => o.WorkNoteDetailPageComponent
            ),
        },
        {
          path: '',
          loadComponent: () =>
            import('./pages/work-note-list-page/work-note-list-page.component').then(
              (o) => o.WorkNoteListPageComponent
            ),
          pathMatch: 'full',
        },
        { path: '**', redirectTo: '' },
      ],
    },
  ],
  canActivate: [AuthenticationGuard, AuthorizationGuard],
  data: {
    permission: SOFTLINE_PERMISSION_WORK_NOTE,
  },
  providers: [
    importProvidersFrom(
      StoreModule.forFeature({
        name: SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        feature: WorkNoteCollectionStore.feature,
        providers: [
          {
            provide: SOFTLINE_STORE_ENTITY_SERVICE,
            useFactory: (service: ConnectionHttpService) =>
              new SoftlineEntityService(service, SOFTLINE_API_WORK_NOTES),
            deps: [SOFTLINE_SERVICE_HTTP],
          },
          {
            provide: WorkNoteService,
            useFactory: (service: ConnectionHttpService) =>
              new WorkNoteService(service, inject(HttpClient)),
            deps: [SOFTLINE_SERVICE_HTTP],
          },
        ],
      }),
      StoreModule.forFeature({
        name: SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
        feature: WorkNoteBewegungenStore.feature,
        providers: [
          {
            provide: SOFTLINE_STORE_ENTITY_SERVICE,
            useFactory: (service: ConnectionHttpService) =>
              new SoftlineEntityService(service, SOFTLINE_API_WORK_NOTE_BEWEGUNGEN),
            deps: [SOFTLINE_SERVICE_HTTP],
          },
          {
            provide: WorkNoteBewegungenService,
            useFactory: (service: ConnectionHttpService) =>
              new WorkNoteBewegungenService(service),
            deps: [SOFTLINE_SERVICE_HTTP],
          },
        ],
      }),
      StoreModule.forFeature({
        name: SOFTLINE_FEATURE_WORK_NOTE_KZWETTER,
        feature: WorkNoteKzWetterStore.feature,
        providers: [
          {
            provide: SOFTLINE_STORE_ENTITY_SERVICE,
            useFactory: (service: ConnectionHttpService) =>
              new SoftlineEntityService(service, SOFTLINE_API_WORK_NOTE_BEWEGUNGEN),
            deps: [SOFTLINE_SERVICE_HTTP],
          },
          {
            provide: KzWetterService,
            useFactory: (service: ConnectionHttpService, client: HttpClient) =>
              new KzWetterService(service, client),
            deps: [SOFTLINE_SERVICE_HTTP, HttpClient],
          },
        ],
      })
    ),
    SignatureService,
  ],
};

export const arbeitsscheinRoutes: Routes = [WORK_NOTE_ROUTE_BASE];

export function workNoteRoutes(config?: ArbeitsscheinConfig): Routes {
  const route = { ...WORK_NOTE_ROUTE_BASE };

  if (config && config?.employeeSelection) {
    route.resolve = {
      masterData: MasterDataResolver,
    };
  }

  return [route];
}
